// Centralized currency formatting for the admin portal.
//
// Mirrors demo-app/src/utils/money.js. ONE currency per store: set once when
// the tenant's general settings load (setStoreCurrency), then every money
// render reads it. Selecting a currency RELABELS amounts (symbol + locale);
// it never converts values.

export const DEFAULT_CURRENCY = 'USD';

// MVP set — all 2-decimal (minorFactor 100).
export const SUPPORTED_CURRENCIES = {
  USD: { symbol: '$', decimals: 2, minorFactor: 100, locale: 'en-US', label: 'US Dollar' },
  INR: { symbol: '₹', decimals: 2, minorFactor: 100, locale: 'en-IN', label: 'Indian Rupee' },
  EUR: { symbol: '€', decimals: 2, minorFactor: 100, locale: 'de-DE', label: 'Euro' },
  GBP: { symbol: '£', decimals: 2, minorFactor: 100, locale: 'en-GB', label: 'British Pound' },
  CAD: { symbol: '$', decimals: 2, minorFactor: 100, locale: 'en-CA', label: 'Canadian Dollar' },
  AUD: { symbol: '$', decimals: 2, minorFactor: 100, locale: 'en-AU', label: 'Australian Dollar' },
  AED: { symbol: 'د.إ', decimals: 2, minorFactor: 100, locale: 'ar-AE', label: 'UAE Dirham' },
};

let _storeCurrency = DEFAULT_CURRENCY;
let _storeLocaleOverride = '';

export function normalizeCurrency(code) {
  const c = String(code || '').trim().toUpperCase();
  return SUPPORTED_CURRENCIES[c] ? c : DEFAULT_CURRENCY;
}

export function setStoreCurrency(code, locale) {
  _storeCurrency = normalizeCurrency(code);
  _storeLocaleOverride = String(locale || '').trim();
}

export function getStoreCurrency() {
  return _storeCurrency;
}

export function getCurrencyMeta(currency) {
  return SUPPORTED_CURRENCIES[normalizeCurrency(currency)];
}

function _resolveLocale(currency, localeArg) {
  if (localeArg) return localeArg;
  if (_storeLocaleOverride && normalizeCurrency(currency) === _storeCurrency) {
    return _storeLocaleOverride;
  }
  return getCurrencyMeta(currency).locale;
}

function _format(amountMajor, currency, locale) {
  const cur = normalizeCurrency(currency);
  const meta = SUPPORTED_CURRENCIES[cur];
  const value = Number.isFinite(Number(amountMajor)) ? Number(amountMajor) : 0;
  try {
    return new Intl.NumberFormat(_resolveLocale(cur, locale), {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: meta.decimals,
      maximumFractionDigits: meta.decimals,
    }).format(value);
  } catch {
    return `${meta.symbol}${value.toFixed(meta.decimals)}`;
  }
}

// Format an integer MINOR-unit amount (cents/paise).
export function formatMinor(minorUnits, currency = _storeCurrency, locale) {
  const meta = getCurrencyMeta(currency);
  return _format((Number(minorUnits) || 0) / meta.minorFactor, currency, locale);
}

// Format a MAJOR-unit amount (e.g. 12.5).
export function formatMajor(amount, currency = _storeCurrency, locale) {
  return _format(amount, currency, locale);
}

export function currencySymbol(currency = _storeCurrency) {
  return getCurrencyMeta(currency).symbol;
}
